import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  decideGrammarDraftAPI,
  fetchGrammarDraftListAPI,
  fetchGrammarSkillTreeAPI,
  generateGrammarItemAPI,
  updateGrammarDraftAPI,
  verifyGrammarDraftAPI,
} from "../grammarMakerAPI";
import "./GrammarMakerPage.css";

// 문법 출제기. 소분류를 고르면 서버가 문항을 만들고, 정답을 가린 채 다시 풀려서 대조한
// 결과까지 붙여 검수 대기열에 쌓는다. 여기서 사람이 보고 고치거나 승인한다.
//
// **화면은 "어느 소분류를 몇 개" 만 보낸다.** 출제 지침·프롬프트·검증 규칙은 전부 서버에
// 있다(`api/echo/gs_grammar_maker.py`). 번들에 넣으면 누구나 내려받을 수 있어서다.

const VERIFY_LABEL = {
  pass: "검수 통과",
  flagged: "검수 걸림",
  unchecked: "미검수",
};

function toEditForm(draft) {
  return {
    instruction: draft.instruction,
    kr: draft.kr,
    stem: draft.stem,
    passage: draft.passage,
    choicesText: draft.choices.join("\n"),
    answer_index: draft.answer_index ?? "",
    answer_text: draft.answer_text,
    acceptedText: draft.accepted_answers.join("\n"),
    explanation: draft.explanation,
  };
}

function toChanges(form) {
  const choices = form.choicesText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const accepted = form.acceptedText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const changes = {
    instruction: form.instruction,
    kr: form.kr,
    stem: form.stem,
    passage: form.passage,
    explanation: form.explanation,
    answer_text: form.answer_text,
  };
  if (choices.length > 0) {
    changes.choices = choices;
    changes.answer_index = Number(form.answer_index);
  }
  if (accepted.length > 0) {
    changes.accepted_answers = accepted;
  }
  return changes;
}

function GrammarMakerPage() {
  const [tree, setTree] = useState(null);
  const [treeError, setTreeError] = useState("");
  const [levelId, setLevelId] = useState("");
  const [onlyShort, setOnlyShort] = useState(true);
  const [selectedSubIdList, setSelectedSubIdList] = useState([]);

  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [countPerSub, setCountPerSub] = useState(5);

  const [running, setRunning] = useState(false);
  const [logList, setLogList] = useState([]);
  const [usage, setUsage] = useState({ input: 0, output: 0 });

  const [draftList, setDraftList] = useState([]);
  const [draftCounts, setDraftCounts] = useState({});
  const [checkedDraftIdList, setCheckedDraftIdList] = useState([]);
  const [deciding, setDeciding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const appendLog = useCallback((kind, text) => {
    setLogList((previous) => [...previous, { kind, text }]);
  }, []);

  const loadTree = useCallback(async () => {
    try {
      const data = await fetchGrammarSkillTreeAPI();
      setTree(data);
      setLevelId((previous) => previous || data.levels[0]?.level_id || "");
      setModel((previous) => previous || data.model_list[0] || "");
    } catch (error) {
      setTreeError(
        error.response?.data?.error || "소분류 목록을 불러오지 못했습니다."
      );
    }
  }, []);

  const loadDraftList = useCallback(async () => {
    try {
      const data = await fetchGrammarDraftListAPI({ status: "draft" });
      setDraftList(data.drafts);
      setDraftCounts(data.counts);
    } catch (error) {
      // 목록만 못 받은 것이라 화면 전체를 막지 않는다.
      setDraftList([]);
    }
  }, []);

  useEffect(() => {
    loadTree();
    loadDraftList();
  }, [loadTree, loadDraftList]);

  const activeLevel = useMemo(
    () => tree?.levels.find((level) => level.level_id === levelId) || null,
    [tree, levelId]
  );

  const visibleCategoryList = useMemo(() => {
    if (!activeLevel) {
      return [];
    }
    return activeLevel.categories
      .map((category) => ({
        ...category,
        sub_items: onlyShort
          ? category.sub_items.filter((subItem) => subItem.short > 0)
          : category.sub_items,
      }))
      .filter((category) => category.sub_items.length > 0);
  }, [activeLevel, onlyShort]);

  const toggleSubId = (subId) => {
    setSelectedSubIdList((previous) =>
      previous.includes(subId)
        ? previous.filter((item) => item !== subId)
        : [...previous, subId]
    );
  };

  const selectAllVisible = () => {
    setSelectedSubIdList(
      visibleCategoryList.flatMap((category) =>
        category.sub_items.map((subItem) => subItem.sub_id)
      )
    );
  };

  const runGeneration = async () => {
    // 서버에 키가 심겨 있으면 화면에서 받지 않아도 된다.
    if (!apiKey.trim() && !tree?.has_stored_api_key) {
      appendLog("error", "API 키를 먼저 입력하세요.");
      return;
    }
    if (selectedSubIdList.length === 0) {
      appendLog("error", "생성할 소분류를 고르세요.");
      return;
    }

    setRunning(true);
    setLogList([]);
    appendLog(
      "ok",
      `시작: 소분류 ${selectedSubIdList.length}개 / 소분류당 ${countPerSub}문항 / ${model}`
    );

    let inputTokens = 0;
    let outputTokens = 0;
    for (const subId of selectedSubIdList) {
      try {
        const result = await generateGrammarItemAPI({
          subId,
          count: countPerSub,
          model,
          apiKey: apiKey.trim(),
        });
        inputTokens += result.usage.input_tokens;
        outputTokens += result.usage.output_tokens;
        setUsage({ input: inputTokens, output: outputTokens });

        const rejectedText = result.rejected.length
          ? ` · 반려 ${result.rejected.length}(${result.rejected[0].reason})`
          : "";
        const duplicatedText = result.duplicated
          ? ` · 중복 ${result.duplicated}`
          : "";
        appendLog(
          result.verified_pass ? "ok" : "warn",
          `${subId} 생성 ${result.saved_count} · 검수 통과 ${result.verified_pass}` +
            `${rejectedText}${duplicatedText}`
        );
      } catch (error) {
        appendLog(
          "error",
          `${subId} 실패: ${error.response?.data?.error || "요청이 실패했습니다."}`
        );
      }
    }

    appendLog("ok", "완료");
    setRunning(false);
    loadTree();
    loadDraftList();
  };

  const toggleDraftId = (draftId) => {
    setCheckedDraftIdList((previous) =>
      previous.includes(draftId)
        ? previous.filter((item) => item !== draftId)
        : [...previous, draftId]
    );
  };

  const decide = async (action, { force = false } = {}) => {
    if (checkedDraftIdList.length === 0) {
      return;
    }
    setDeciding(true);
    try {
      const result = await decideGrammarDraftAPI(action, checkedDraftIdList, {
        force,
      });
      if (action === "approve") {
        appendLog(
          result.blocked?.length ? "warn" : "ok",
          `승인 ${result.approved}건` +
            (result.blocked?.length
              ? ` · 검수를 통과하지 못해 막힌 것 ${result.blocked.length}건`
              : "")
        );
      }
      setCheckedDraftIdList([]);
      await loadDraftList();
      await loadTree();
    } catch (error) {
      appendLog(
        "error",
        `검수 실패: ${error.response?.data?.error || "요청이 실패했습니다."}`
      );
    }
    setDeciding(false);
  };

  const verifySelected = async () => {
    if (checkedDraftIdList.length === 0) {
      return;
    }
    if (!apiKey.trim() && !tree?.has_stored_api_key) {
      appendLog("error", "재검수에도 API 키가 필요합니다.");
      return;
    }
    setDeciding(true);
    try {
      const result = await verifyGrammarDraftAPI(
        checkedDraftIdList,
        apiKey.trim()
      );
      appendLog("ok", `재검수 ${result.checked}건`);
      await loadDraftList();
    } catch (error) {
      appendLog(
        "error",
        `재검수 실패: ${error.response?.data?.error || "요청이 실패했습니다."}`
      );
    }
    setDeciding(false);
  };

  const startEdit = (draft) => {
    setEditingId(draft.id);
    setEditForm(toEditForm(draft));
  };

  const changeEditField = (field, value) => {
    setEditForm((previous) => {
      const next = { ...previous, [field]: value };
      // 정답 번호를 바꾸면 정답 텍스트도 같이 맞춘다. 손으로 고치다가 둘을 어긋나게
      // 두는 일이 잦고, 그대로 두면 학생이 맞혀도 오답 처리된다.
      if (field === "answer_index") {
        const choices = next.choicesText.split("\n").map((line) => line.trim());
        const picked = choices[Number(value)];
        if (picked) {
          next.answer_text = picked;
        }
      }
      return next;
    });
  };

  const saveEdit = async () => {
    setDeciding(true);
    try {
      await updateGrammarDraftAPI(editingId, toChanges(editForm));
      setEditingId(null);
      setEditForm(null);
      await loadDraftList();
      appendLog("ok", "고쳤습니다. 재검수를 받으면 승인할 수 있습니다.");
    } catch (error) {
      appendLog(
        "error",
        error.response?.data?.error || "고친 내용을 저장하지 못했습니다."
      );
    }
    setDeciding(false);
  };

  if (treeError) {
    return (
      <div className="page-container">
        <div className="grammar-maker-error">{treeError}</div>
      </div>
    );
  }

  if (!tree) {
    return (
      <div className="page-container">
        <div className="grammar-maker-empty">불러오는 중입니다.</div>
      </div>
    );
  }

  const passCount = draftList.filter(
    (draft) => draft.verify_status === "pass"
  ).length;

  return (
    <div className="page-container grammar-maker">
      <div className="grammar-maker-summary">
        <div className="grammar-maker-summary-item">
          <span className="grammar-maker-summary-label">전체 소분류</span>
          <strong>{tree.summary.sub_total}</strong>
        </div>
        <div className="grammar-maker-summary-item grammar-maker-summary-item--warn">
          <span className="grammar-maker-summary-label">문항 0개</span>
          <strong>{tree.summary.empty}</strong>
        </div>
        <div className="grammar-maker-summary-item grammar-maker-summary-item--warn">
          <span className="grammar-maker-summary-label">
            {tree.summary.enough}개 미만
          </span>
          <strong>{tree.summary.short}</strong>
        </div>
        <div className="grammar-maker-summary-item">
          <span className="grammar-maker-summary-label">검수 대기</span>
          <strong>{draftCounts.draft ?? tree.draft_waiting}</strong>
        </div>
      </div>

      <div className="grammar-maker-controls">
        <label className="grammar-maker-field">
          <span>
            API 키
            {tree.has_stored_api_key ? " (서버에 있음, 비워 두면 그것을 씁니다)" : ""}
          </span>
          <input
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder={
              tree.has_stored_api_key ? "비워 두면 서버 키" : "sk-ant-..."
            }
            autoComplete="off"
          />
        </label>
        <label className="grammar-maker-field grammar-maker-field--narrow">
          <span>생성 모델</span>
          <select value={model} onChange={(event) => setModel(event.target.value)}>
            {tree.model_list.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="grammar-maker-field grammar-maker-field--narrow">
          <span>소분류당 문항</span>
          <input
            type="number"
            min="1"
            max={tree.max_item_per_call}
            value={countPerSub}
            onChange={(event) => setCountPerSub(Number(event.target.value))}
          />
        </label>
        <button
          type="button"
          className="button-outline grammar-maker-run"
          onClick={runGeneration}
          disabled={running}
        >
          {running
            ? "생성 중..."
            : `선택한 ${selectedSubIdList.length}개 소분류 생성`}
        </button>
      </div>
      <p className="grammar-maker-note">
        만든 문항은 정답을 가린 채 다시 풀려서 대조합니다. 검수를 통과하지 못하면
        승인되지 않습니다. 화면에 키를 넣으면 그 요청에만 쓰이고 저장하지 않습니다.
      </p>

      <div className="grammar-maker-level-row">
        {tree.levels.map((level) => (
          <button
            key={level.level_id}
            type="button"
            className={
              level.level_id === levelId
                ? "grammar-maker-level grammar-maker-level--on"
                : "grammar-maker-level"
            }
            onClick={() => setLevelId(level.level_id)}
          >
            {level.level_label}
          </button>
        ))}
        <label className="grammar-maker-check">
          <input
            type="checkbox"
            checked={onlyShort}
            onChange={(event) => setOnlyShort(event.target.checked)}
          />
          모자란 소분류만
        </label>
        <button type="button" className="button-outline" onClick={selectAllVisible}>
          보이는 것 전체 선택
        </button>
        <button
          type="button"
          className="button-outline"
          onClick={() => setSelectedSubIdList([])}
        >
          선택 해제
        </button>
      </div>

      <div className="grammar-maker-panes">
        <div className="grammar-maker-pane">
          <h3 className="grammar-maker-pane-title">소분류</h3>
          <div className="grammar-maker-sub-box">
            {visibleCategoryList.map((category) => (
              <div key={category.category_id}>
                <div className="grammar-maker-category">
                  {category.category_label}
                  <span className="grammar-maker-category-level">
                    {category.g_level}
                  </span>
                </div>
                {category.sub_items.map((subItem) => (
                  <label key={subItem.sub_id} className="grammar-maker-sub-row">
                    <input
                      type="checkbox"
                      checked={selectedSubIdList.includes(subItem.sub_id)}
                      onChange={() => toggleSubId(subItem.sub_id)}
                    />
                    <span className="grammar-maker-sub-label">
                      {subItem.sub_label}
                    </span>
                    <span className="grammar-maker-sub-id">{subItem.sub_id}</span>
                    <span
                      className={
                        subItem.item_count === 0
                          ? "badge grammar-maker-count grammar-maker-count--zero"
                          : "badge grammar-maker-count"
                      }
                    >
                      {subItem.item_count}개
                    </span>
                  </label>
                ))}
              </div>
            ))}
            {visibleCategoryList.length === 0 && (
              <div className="grammar-maker-empty">
                이 레벨에는 모자란 소분류가 없습니다.
              </div>
            )}
          </div>
        </div>

        <div className="grammar-maker-pane">
          <h3 className="grammar-maker-pane-title">
            진행
            {(usage.input > 0 || usage.output > 0) && (
              <span className="grammar-maker-usage">
                입력 {usage.input.toLocaleString()} · 출력{" "}
                {usage.output.toLocaleString()} 토큰
              </span>
            )}
          </h3>
          <div className="grammar-maker-log">
            {logList.map((line, index) => (
              <div
                key={`${line.text}-${index}`}
                className={`grammar-maker-log-line grammar-maker-log-line--${line.kind}`}
              >
                {line.text}
              </div>
            ))}
            {logList.length === 0 && (
              <div className="grammar-maker-empty">
                아직 생성한 것이 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grammar-maker-draft-head">
        <h3 className="grammar-maker-pane-title">
          검수 대기 {draftList.length}건
          <span className="grammar-maker-usage">검수 통과 {passCount}건</span>
        </h3>
        <button
          type="button"
          className="button-outline"
          onClick={verifySelected}
          disabled={deciding || checkedDraftIdList.length === 0}
        >
          선택 {checkedDraftIdList.length}건 재검수
        </button>
        <button
          type="button"
          className="button-outline"
          onClick={() => decide("approve")}
          disabled={deciding || checkedDraftIdList.length === 0}
        >
          선택 승인
        </button>
        <button
          type="button"
          className="button-outline"
          onClick={() => decide("reject")}
          disabled={deciding || checkedDraftIdList.length === 0}
        >
          선택 반려
        </button>
        <span className="grammar-maker-note">
          검수를 통과한 것만 승인됩니다.
        </span>
      </div>

      <div className="grammar-maker-draft-list">
        {draftList.map((draft) => (
          <div key={draft.id} className="grammar-maker-draft">
            <label className="grammar-maker-draft-top">
              <input
                type="checkbox"
                checked={checkedDraftIdList.includes(draft.id)}
                onChange={() => toggleDraftId(draft.id)}
              />
              <span
                className={`badge grammar-maker-verify grammar-maker-verify--${draft.verify_status}`}
              >
                {VERIFY_LABEL[draft.verify_status] || draft.verify_status}
              </span>
              <span className="badge">{draft.item_type}</span>
              <span className="grammar-maker-draft-type">
                {draft.item_type_label}
              </span>
              <span className="grammar-maker-sub-id">
                {draft.sub_id} · {draft.sub_label}
              </span>
              <span className="grammar-maker-sub-id">난이도 {draft.difficulty}</span>
              {draft.edited && (
                <span className="grammar-maker-sub-id">고침</span>
              )}
              {draft.repaired && (
                <span className="grammar-maker-repaired">보정: {draft.repaired}</span>
              )}
            </label>

            {draft.verify_note && (
              <div className="grammar-maker-verify-note">{draft.verify_note}</div>
            )}

            {editingId === draft.id ? (
              <div className="grammar-maker-draft-body">
                <label className="grammar-maker-edit-field">
                  <span>발문</span>
                  <input
                    type="text"
                    value={editForm.instruction}
                    onChange={(event) =>
                      changeEditField("instruction", event.target.value)
                    }
                  />
                </label>
                <label className="grammar-maker-edit-field">
                  <span>본문</span>
                  <textarea
                    rows="3"
                    value={editForm.passage || editForm.stem}
                    onChange={(event) =>
                      changeEditField(
                        editForm.passage ? "passage" : "stem",
                        event.target.value
                      )
                    }
                  />
                </label>
                {editForm.choicesText && (
                  <>
                    <label className="grammar-maker-edit-field">
                      <span>선택지 (한 줄에 하나)</span>
                      <textarea
                        rows="4"
                        value={editForm.choicesText}
                        onChange={(event) =>
                          changeEditField("choicesText", event.target.value)
                        }
                      />
                    </label>
                    <label className="grammar-maker-edit-field grammar-maker-edit-field--narrow">
                      <span>정답 번호 (0부터)</span>
                      <input
                        type="number"
                        min="0"
                        value={editForm.answer_index}
                        onChange={(event) =>
                          changeEditField("answer_index", event.target.value)
                        }
                      />
                    </label>
                  </>
                )}
                <label className="grammar-maker-edit-field">
                  <span>정답</span>
                  <input
                    type="text"
                    value={editForm.answer_text}
                    onChange={(event) =>
                      changeEditField("answer_text", event.target.value)
                    }
                  />
                </label>
                {editForm.acceptedText && (
                  <label className="grammar-maker-edit-field">
                    <span>허용 답안 (한 줄에 하나)</span>
                    <textarea
                      rows="2"
                      value={editForm.acceptedText}
                      onChange={(event) =>
                        changeEditField("acceptedText", event.target.value)
                      }
                    />
                  </label>
                )}
                <label className="grammar-maker-edit-field">
                  <span>해설</span>
                  <textarea
                    rows="2"
                    value={editForm.explanation}
                    onChange={(event) =>
                      changeEditField("explanation", event.target.value)
                    }
                  />
                </label>
                <div className="grammar-maker-edit-buttons">
                  <button
                    type="button"
                    className="button-outline"
                    onClick={saveEdit}
                    disabled={deciding}
                  >
                    저장
                  </button>
                  <button
                    type="button"
                    className="button-outline"
                    onClick={() => {
                      setEditingId(null);
                      setEditForm(null);
                    }}
                  >
                    취소
                  </button>
                  <span className="grammar-maker-note">
                    고치면 미검수로 돌아갑니다. 재검수를 받아야 승인됩니다.
                  </span>
                </div>
              </div>
            ) : (
              <div className="grammar-maker-draft-body">
                <div className="grammar-maker-instruction">{draft.instruction}</div>
                {draft.kr && <div className="grammar-maker-kr">{draft.kr}</div>}
                {(draft.passage || draft.stem) && (
                  <div className="grammar-maker-stem">
                    {draft.passage || draft.stem}
                  </div>
                )}
                {draft.choices.length > 0 && (
                  <ol className="grammar-maker-choice-list">
                    {draft.choices.map((choice, index) => (
                      <li
                        key={`${draft.id}-${index}`}
                        className={
                          index === draft.answer_index
                            ? "grammar-maker-choice grammar-maker-choice--answer"
                            : "grammar-maker-choice"
                        }
                      >
                        {choice}
                      </li>
                    ))}
                  </ol>
                )}
                <div className="grammar-maker-answer">정답: {draft.answer_text}</div>
                {draft.accepted_answers.length > 0 && (
                  <div className="grammar-maker-answer">
                    허용: {draft.accepted_answers.join(" / ")}
                  </div>
                )}
                <div className="grammar-maker-explanation">{draft.explanation}</div>
                <button
                  type="button"
                  className="button-outline grammar-maker-edit-open"
                  onClick={() => startEdit(draft)}
                >
                  고치기
                </button>
              </div>
            )}
          </div>
        ))}
        {draftList.length === 0 && (
          <div className="grammar-maker-empty">검수할 초안이 없습니다.</div>
        )}
      </div>
    </div>
  );
}

export default GrammarMakerPage;
